import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createApiClient, type ApiClient, type ApiClientConfig } from "./index";

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ config, children }: { config: ApiClientConfig; children: ReactNode }) {
  const client = useMemo(() => createApiClient(config), [config]);
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const c = useContext(ApiContext);
  if (!c) throw new Error("useApi must be used inside <ApiProvider>");
  return c;
}

export interface Loaded<T> {
  state: "loading" | "ready" | "error";
  data?: T;
  error?: Error;
  reload: () => void;
}

/** Small data hook: loads once per dependency change, exposes reload. Enough for these screens; no cache library. */
export function useLoad<T>(load: (api: ApiClient) => Promise<T>, deps: unknown[] = []): Loaded<T> {
  const api = useApi();
  const [tick, setTick] = useState(0);
  const [result, setResult] = useState<Omit<Loaded<T>, "reload">>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setResult((r) => (r.state === "ready" ? r : { state: "loading" }));
    load(api)
      .then((data) => live && setResult({ state: "ready", data }))
      .catch((e: unknown) => live && setResult({ state: "error", error: e instanceof Error ? e : new Error(String(e)) }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tick, ...deps]);
  return { ...result, reload: () => setTick((t) => t + 1) };
}
